import React from "react";
import { slugHue } from "../../../lib/marketplace";

/** Marketplace product detail pages are owned by apps/marketplace, not this app. */
const MARKETPLACE_ORIGIN = "https://marketplace.limelite.store";

function formatPrice(price, currency) {
  if (price === null || price === undefined) return null;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(price);
  } catch {
    // An ingested currency code we can't format is not worth blanking the card over.
    return `${price} ${currency || ""}`.trim();
  }
}

/**
 * A single product from the cross-store index — the "jackets" half of a search.
 *
 * Taller and narrower than an AppCard on purpose: a product shelf should not be
 * mistaken for the app grid it sits above.
 */
export default function ProductCard({ product }) {
  const price = formatPrice(product.price, product.currency);
  const hue = slugHue(product.slug || product.name);
  const href = `${MARKETPLACE_ORIGIN}/product/${product.slug}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col border border-white/10 bg-[#0d0d10] transition-all duration-200 hover:-translate-y-0.5 hover:border-lime-500/40"
    >
      <div className="relative aspect-square w-full overflow-hidden bg-black">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center"
            style={{ background: `linear-gradient(145deg, hsl(${hue} 38% 15%) 0%, #08080a 70%)` }}
          >
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/25">
              No image
            </span>
          </div>
        )}
        {product.inStock === false && (
          <span className="absolute right-0 top-0 bg-black/80 px-2 py-1 text-[10px] uppercase tracking-wider text-silver-dark">
            Sold out
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1 p-2.5">
        <h3 className="line-clamp-2 text-xs font-medium leading-snug text-silver-light">
          {product.name}
        </h3>
        {product.storeSlug && (
          <p className="truncate text-[10px] text-silver-dark">{product.storeSlug}</p>
        )}
        {price && (
          <p className="mt-auto pt-1 text-sm font-bold tabular-nums text-lime-300">{price}</p>
        )}
      </div>
    </a>
  );
}
